import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { eventAPI, sponsorAPI, resolveAssetUrl, exportAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft,
  Calendar as CalendarIcon,
  Plus,
  X,
  Loader2,
  Save,
  User,
  BookOpen,
  Building2,
  Award,
  CreditCard,
  FileDown,
  Copy
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { cn } from '../lib/utils';

export default function EventForm() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const isEditing = !!eventId;

  const [loading, setLoading] = useState(false);
  const [fetchingEvent, setFetchingEvent] = useState(isEditing);
  const [date, setDate] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  // "Copy tiers from another event" picker state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySourceEvents, setCopySourceEvents] = useState([]);
  const [copyLoading, setCopyLoading] = useState(false);
  const [pendingCopySource, setPendingCopySource] = useState(null); // event picked, awaiting replace/append confirm
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    time: '18:00',
    venue: '',
    duration_hours: 2.0,
    speakers: [{ name: '', title: '', organization: '' }],
    topics: [''],
    learning_objectives: [''],  // Optional, up to 7
    educational_statement: 'This activity is relevant to general practice and may be self-recorded under Educational Activities in accordance with RACGP CPD requirements.',
    org_name: '',
    org_signatory: '',
    org_signatory_title: '',
    sponsor_ids: [],
    // Per-event custom sponsorship tiers — array of { name, amount, benefits (multiline string) }.
    // Each tier is fully editable: name, amount, and benefits are all custom.
    sponsorshipTiers: [],
    payment_details: '',
    iida_member_discount_percent: 0,
    iida_member_discount_amount: 0,
  });

  useEffect(() => {
    fetchSponsors();
    if (isEditing) {
      fetchEvent();
    }
  }, [eventId]);

  const fetchSponsors = async () => {
    try {
      const response = await sponsorAPI.list();
      setSponsors(response.data);
    } catch (error) {
      console.error('Failed to fetch sponsors');
    }
  };

  const fetchEvent = async () => {
    try {
      const response = await eventAPI.get(eventId);
      const event = response.data;
      // Hydrate per-event custom tiers from the saved sponsorship_tiers array
      const sponsorshipTiers = (event.sponsorship_tiers || []).map((t) => ({
        name: t.tier_name || '',
        amount: t.amount ?? '',
        benefits: (t.benefits || []).join('\n'),
      }));
      setFormData({
        title: event.title,
        description: event.description || '',
        time: event.time,
        venue: event.venue,
        duration_hours: event.duration_hours,
        speakers: event.speakers?.length > 0 ? event.speakers : [{ name: '', title: '', organization: '' }],
        topics: event.topics?.length > 0 ? event.topics : [''],
        learning_objectives: event.learning_objectives?.length > 0 ? event.learning_objectives : [''],
        educational_statement: event.educational_statement || 'This activity is relevant to general practice and may be self-recorded under Educational Activities in accordance with RACGP CPD requirements.',
        org_name: event.org_name || '',
        org_signatory: event.org_signatory || '',
        org_signatory_title: event.org_signatory_title || '',
        sponsor_ids: event.sponsor_ids || [],
        sponsorshipTiers,
        payment_details: event.payment_details || '',
        iida_member_discount_percent: event.iida_member_discount_percent ?? 0,
        iida_member_discount_amount: event.iida_member_discount_amount ?? 0,
      });
      setDate(parseISO(event.date));
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/events');
    } finally {
      setFetchingEvent(false);
    }
  };

  // Per-event custom tier handlers
  const addTier = () => {
    setFormData((prev) => ({
      ...prev,
      sponsorshipTiers: [...prev.sponsorshipTiers, { name: '', amount: '', benefits: '' }],
    }));
  };

  const removeTier = (index) => {
    setFormData((prev) => ({
      ...prev,
      sponsorshipTiers: prev.sponsorshipTiers.filter((_, i) => i !== index),
    }));
  };

  const updateTier = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.sponsorshipTiers];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, sponsorshipTiers: next };
    });
  };

  // "Copy tiers from another event" — open picker, fetch list lazily
  const openCopyDialog = async () => {
    setCopyDialogOpen(true);
    if (copySourceEvents.length > 0) return; // already loaded
    setCopyLoading(true);
    try {
      const res = await eventAPI.list();
      const candidates = (res.data || [])
        .filter((e) => e.id !== eventId && (e.sponsorship_tiers || []).length > 0)
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setCopySourceEvents(candidates);
    } catch (e) {
      toast.error('Failed to load events');
    } finally {
      setCopyLoading(false);
    }
  };

  // Convert backend tier shape → form tier shape
  const _hydrateTiers = (sponsorshipTiers) =>
    (sponsorshipTiers || []).map((t) => ({
      name: t.tier_name || '',
      amount: t.amount ?? '',
      benefits: (t.benefits || []).join('\n'),
    }));

  const handleCopySourceSelect = (sourceEvent) => {
    // If current event has no tiers, just paste directly.
    if (formData.sponsorshipTiers.length === 0) {
      setFormData((prev) => ({
        ...prev,
        sponsorshipTiers: _hydrateTiers(sourceEvent.sponsorship_tiers),
      }));
      setCopyDialogOpen(false);
      toast.success(`Copied ${(sourceEvent.sponsorship_tiers || []).length} tiers from "${sourceEvent.title}"`);
      return;
    }
    // Otherwise stash the source and open replace/append confirm
    setPendingCopySource(sourceEvent);
    setCopyDialogOpen(false);
  };

  const applyCopy = (mode) => {
    if (!pendingCopySource) return;
    const incoming = _hydrateTiers(pendingCopySource.sponsorship_tiers);
    setFormData((prev) => ({
      ...prev,
      sponsorshipTiers: mode === 'replace' ? incoming : [...prev.sponsorshipTiers, ...incoming],
    }));
    const count = incoming.length;
    const verb = mode === 'replace' ? 'Replaced with' : 'Appended';
    toast.success(`${verb} ${count} tier${count === 1 ? '' : 's'} from "${pendingCopySource.title}"`);
    setPendingCopySource(null);
  };

  const toggleSponsor = (sponsorId) => {
    setFormData(prev => ({
      ...prev,
      sponsor_ids: prev.sponsor_ids.includes(sponsorId)
        ? prev.sponsor_ids.filter(id => id !== sponsorId)
        : [...prev.sponsor_ids, sponsorId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Please select a date');
      return;
    }

    // Filter out empty values
    const filteredSpeakers = formData.speakers.filter(s => s.name.trim() !== '');
    const filteredTopics = formData.topics.filter(t => t.trim() !== '');
    const filteredObjectives = formData.learning_objectives.filter(obj => obj.trim() !== '');
    
    // Validate max 7 learning objectives
    if (filteredObjectives.length > 7) {
      toast.error('Maximum 7 learning objectives allowed');
      return;
    }

    setLoading(true);
    try {
      // Build sponsorship_tiers from the custom per-event tier list.
      // Drop empty rows; keep entries that have either a name+amount or any benefits.
      const sponsorship_tiers = (formData.sponsorshipTiers || [])
        .map((t) => {
          const name = (t.name || '').trim();
          const amount = parseFloat(t.amount) || 0;
          const benefits = (t.benefits || '')
            .split('\n')
            .map((b) => b.trim())
            .filter(Boolean);
          if (!name && amount <= 0 && benefits.length === 0) return null;
          return { tier_name: name || 'Sponsorship tier', amount, benefits };
        })
        .filter(Boolean);

      const { sponsorshipTiers: _omit, ...rest } = formData;
      const eventData = {
        ...rest,
        date: format(date, 'yyyy-MM-dd'),
        speakers: filteredSpeakers,
        topics: filteredTopics,
        learning_objectives: filteredObjectives,
        sponsorship_tiers,
        iida_member_discount_percent: parseFloat(formData.iida_member_discount_percent) || 0,
        iida_member_discount_amount: parseFloat(formData.iida_member_discount_amount) || 0,
      };

      let savedId = eventId;
      if (isEditing) {
        await eventAPI.update(eventId, eventData);
        toast.success('Event updated successfully');
      } else {
        const created = await eventAPI.create(eventData);
        savedId = created.data?.id;
        toast.success('Event created successfully');
      }
      // Navigate to the full event detail page (not back to the truncated card on /events)
      if (savedId) {
        navigate(`/events/${savedId}`);
      } else {
        navigate('/events');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  // Speaker handlers
  const addSpeaker = () => {
    if (formData.speakers.length < 5) {
      setFormData({
        ...formData,
        speakers: [...formData.speakers, { name: '', title: '', organization: '' }]
      });
    }
  };

  const removeSpeaker = (index) => {
    const newSpeakers = formData.speakers.filter((_, i) => i !== index);
    setFormData({ ...formData, speakers: newSpeakers });
  };

  const updateSpeaker = (index, field, value) => {
    const newSpeakers = [...formData.speakers];
    newSpeakers[index] = { ...newSpeakers[index], [field]: value };
    setFormData({ ...formData, speakers: newSpeakers });
  };

  // Topic handlers
  const addTopic = () => {
    if (formData.topics.length < 10) {
      setFormData({
        ...formData,
        topics: [...formData.topics, '']
      });
    }
  };

  const removeTopic = (index) => {
    const newTopics = formData.topics.filter((_, i) => i !== index);
    setFormData({ ...formData, topics: newTopics });
  };

  const updateTopic = (index, value) => {
    const newTopics = [...formData.topics];
    newTopics[index] = value;
    setFormData({ ...formData, topics: newTopics });
  };

  // Learning objective handlers
  const addObjective = () => {
    if (formData.learning_objectives.length < 7) {
      setFormData({
        ...formData,
        learning_objectives: [...formData.learning_objectives, '']
      });
    }
  };

  const removeObjective = (index) => {
    const newObjectives = formData.learning_objectives.filter((_, i) => i !== index);
    setFormData({ ...formData, learning_objectives: newObjectives });
  };

  const updateObjective = (index, value) => {
    const newObjectives = [...formData.learning_objectives];
    newObjectives[index] = value;
    setFormData({ ...formData, learning_objectives: newObjectives });
  };

  if (fetchingEvent) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/events')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isEditing ? 'Edit Event' : 'Create New Event'}
            </h1>
            <p className="text-muted-foreground">
              {isEditing ? 'Update event details' : 'Set up a new educational activity'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Details */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg">Event Details</CardTitle>
              <CardDescription>Basic information about the educational activity</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Evidence-Based Imaging-Guided Interventions in Arthritis"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="h-11"
                  data-testid="event-title-input"
                />
                <p className="text-xs text-muted-foreground">
                  Use evidence-based educational framing, avoid promotional language
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of the educational activity..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="event-description-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full h-11 justify-start text-left font-normal",
                          !date && "text-muted-foreground"
                        )}
                        data-testid="event-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {date && (() => {
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const picked = new Date(date); picked.setHours(0, 0, 0, 0);
                    if (picked < today) {
                      return (
                        <p
                          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-center gap-1.5"
                          data-testid="event-date-past-warning"
                        >
                          <span aria-hidden>⚠</span>
                          This date is in the past. The event will be saved as <strong>completed</strong>.
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="time">Time *</Label>
                  <Input
                    id="time"
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    required
                    className="h-11"
                    data-testid="event-time-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="venue">Venue *</Label>
                  <Input
                    id="venue"
                    placeholder="e.g., Infinity Imaging, Level 2, 123 Medical St"
                    value={formData.venue}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    required
                    className="h-11"
                    data-testid="event-venue-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration">Total Duration (hours) *</Label>
                  <Input
                    id="duration"
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="8"
                    value={formData.duration_hours}
                    onChange={(e) => setFormData({ ...formData, duration_hours: parseFloat(e.target.value) })}
                    required
                    className="h-11"
                    data-testid="event-duration-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Speakers */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                Speakers
              </CardTitle>
              <CardDescription>Add presenters for this event</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.speakers.map((speaker, index) => (
                <div key={index} className="p-4 border border-border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Speaker {index + 1}</span>
                    {formData.speakers.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSpeaker(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Input
                      placeholder="Name"
                      value={speaker.name}
                      onChange={(e) => updateSpeaker(index, 'name', e.target.value)}
                      data-testid={`speaker-name-${index}`}
                    />
                    <Input
                      placeholder="Title (e.g., Dr, Prof)"
                      value={speaker.title}
                      onChange={(e) => updateSpeaker(index, 'title', e.target.value)}
                    />
                    <Input
                      placeholder="Organization"
                      value={speaker.organization}
                      onChange={(e) => updateSpeaker(index, 'organization', e.target.value)}
                    />
                  </div>
                </div>
              ))}

              {formData.speakers.length < 5 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addSpeaker}
                  className="w-full gap-2"
                  data-testid="add-speaker-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Speaker
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Topics Discussed */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Topics Discussed
              </CardTitle>
              <CardDescription>List the main topics covered in this event</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {formData.topics.map((topic, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-shrink-0 w-8 h-11 flex items-center justify-center text-sm font-medium text-muted-foreground">
                    {index + 1}.
                  </div>
                  <Input
                    placeholder="e.g., Current evidence for PRP therapy in osteoarthritis"
                    value={topic}
                    onChange={(e) => updateTopic(index, e.target.value)}
                    className="h-11 flex-1"
                    data-testid={`topic-${index}`}
                  />
                  {formData.topics.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTopic(index)}
                      className="flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}

              {formData.topics.length < 10 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addTopic}
                  className="w-full mt-2 gap-2"
                  data-testid="add-topic-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Topic
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Learning Objectives - Optional */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg">Learning Objectives (Optional)</CardTitle>
              <CardDescription>
                Add up to 7 measurable learning objectives. These strengthen CPD compliance.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground mb-4">
                Start each objective with: "By the end of this session, participants will be able to..."
              </p>
              
              {formData.learning_objectives.map((objective, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-shrink-0 w-8 h-11 flex items-center justify-center text-sm font-medium text-muted-foreground">
                    {index + 1}.
                  </div>
                  <Input
                    placeholder="e.g., Identify appropriate referral indications for imaging-guided interventions"
                    value={objective}
                    onChange={(e) => updateObjective(index, e.target.value)}
                    className="h-11 flex-1"
                    data-testid={`learning-objective-${index}`}
                  />
                  {formData.learning_objectives.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeObjective(index)}
                      className="flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}

              {formData.learning_objectives.length < 7 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addObjective}
                  className="w-full mt-2 gap-2"
                  data-testid="add-objective-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Learning Objective ({formData.learning_objectives.length}/7)
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Educational Statement */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg">Educational Activity Statement</CardTitle>
              <CardDescription>
                This statement appears on certificates for RACGP compliance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.educational_statement}
                onChange={(e) => setFormData({ ...formData, educational_statement: e.target.value })}
                rows={3}
                data-testid="educational-statement-input"
              />
            </CardContent>
          </Card>

          {/* Organization Details (Override Branding) */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg">Organization Details (Optional Override)</CardTitle>
              <CardDescription>
                Leave blank to use default branding settings. Fill in to override for this event.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org_name">Organization Name</Label>
                <Input
                  id="org_name"
                  placeholder="Leave blank to use default"
                  value={formData.org_name}
                  onChange={(e) => setFormData({ ...formData, org_name: e.target.value })}
                  className="h-11"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org_signatory">Signatory Name</Label>
                  <Input
                    id="org_signatory"
                    placeholder="e.g., IIDA Educational Convenor"
                    value={formData.org_signatory}
                    onChange={(e) => setFormData({ ...formData, org_signatory: e.target.value })}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org_signatory_title">Signatory Title</Label>
                  <Input
                    id="org_signatory_title"
                    placeholder="e.g., Medical Director"
                    value={formData.org_signatory_title}
                    onChange={(e) => setFormData({ ...formData, org_signatory_title: e.target.value })}
                    className="h-11"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Event Sponsors */}
          {sponsors.length > 0 && (
            <Card className="mb-6 border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Event Sponsors
                </CardTitle>
                <CardDescription>
                  Select sponsors associated with this event
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {sponsors.map((sponsor) => (
                    <label
                      key={sponsor.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                        formData.sponsor_ids.includes(sponsor.id)
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      )}
                    >
                      <Checkbox
                        checked={formData.sponsor_ids.includes(sponsor.id)}
                        onCheckedChange={() => toggleSponsor(sponsor.id)}
                        data-testid={`sponsor-checkbox-${sponsor.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{sponsor.name}</p>
                        {sponsor.tagline && (
                          <p className="text-xs text-muted-foreground truncate">{sponsor.tagline}</p>
                        )}
                      </div>
                      {sponsor.logo_url && (
                        <img 
                          src={resolveAssetUrl(sponsor.logo_url)} 
                          alt={sponsor.name}
                          className="h-8 max-w-[60px] object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per-Event Custom Sponsorship Tiers */}
          <Card className="mb-6 border-border" data-testid="event-tiers-card">
            <CardHeader className="flex flex-row items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Sponsorship Tiers for this Event
                </CardTitle>
                <CardDescription>
                  Add as many custom tiers as you like. Each tier has its own name, amount and benefits — these flow straight into the Sponsorship Prospectus PDF.
                </CardDescription>
              </div>
              {isEditing && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={async () => {
                    try {
                      await exportAPI.sponsorshipProspectusPdf(eventId, formData.title || 'event');
                      toast.success('Sponsorship prospectus downloaded');
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to generate prospectus PDF');
                    }
                  }}
                  data-testid="download-prospectus-btn"
                >
                  <FileDown className="w-4 h-4" />
                  Download Sponsorship Prospectus (PDF)
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {!isEditing && (
                <div className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-md px-3 py-2">
                  Save this event first, then return to download the sponsorship prospectus PDF.
                </div>
              )}

              {formData.sponsorshipTiers.length === 0 && (
                <div
                  className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center"
                  data-testid="empty-tiers-hint"
                >
                  No tiers yet. Add a tier below (e.g. <strong>Diamond</strong>, <strong>Platinum</strong>, <strong>Headline Partner</strong>) and set its amount and benefits.
                </div>
              )}

              {formData.sponsorshipTiers.map((tier, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg border border-border bg-muted/30 space-y-3"
                  data-testid={`tier-row-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">
                      Tier {index + 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTier(index)}
                      aria-label="Remove tier"
                      data-testid={`remove-tier-${index}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-[1fr_160px] gap-3">
                    <div className="space-y-1">
                      <Label htmlFor={`tier-name-${index}`} className="text-xs text-muted-foreground">
                        Tier name *
                      </Label>
                      <Input
                        id={`tier-name-${index}`}
                        placeholder="e.g. Diamond, Platinum, Headline Partner"
                        value={tier.name}
                        onChange={(e) => updateTier(index, 'name', e.target.value)}
                        className="h-9"
                        data-testid={`tier-name-input-${index}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`tier-amount-${index}`} className="text-xs text-muted-foreground">
                        Amount (AUD ex-GST)
                      </Label>
                      <Input
                        id={`tier-amount-${index}`}
                        type="number"
                        min="0"
                        step="100"
                        placeholder="0"
                        value={tier.amount}
                        onChange={(e) => updateTier(index, 'amount', e.target.value)}
                        className="h-9"
                        data-testid={`tier-amount-input-${index}`}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`tier-benefits-${index}`} className="text-xs text-muted-foreground">
                      Benefits (one per line)
                    </Label>
                    <Textarea
                      id={`tier-benefits-${index}`}
                      placeholder={`Logo on event materials\nVerbal acknowledgment\nDedicated thank-you in attendee certificates`}
                      rows={3}
                      value={tier.benefits}
                      onChange={(e) => updateTier(index, 'benefits', e.target.value)}
                      data-testid={`tier-benefits-input-${index}`}
                    />
                  </div>
                </div>
              ))}

              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addTier}
                  className="flex-1 gap-2"
                  data-testid="add-tier-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Sponsorship Tier
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={openCopyDialog}
                  className="flex-1 gap-2"
                  data-testid="copy-tiers-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy from another event
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* IIDA Member Discount config */}
          <Card className="mb-6 border-border" data-testid="iida-member-discount-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5" />
                IIDA Member Discount
              </CardTitle>
              <CardDescription>
                Discount automatically applied on the public Sponsorship EOI form when a prospective sponsor declares themselves an IIDA member. If both fields are set, the percentage takes precedence.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="iida-discount-pct" className="text-xs text-muted-foreground">
                  Percentage discount (%)
                </Label>
                <Input
                  id="iida-discount-pct"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  placeholder="e.g. 10"
                  value={formData.iida_member_discount_percent}
                  onChange={(e) => setFormData({ ...formData, iida_member_discount_percent: e.target.value })}
                  data-testid="iida-discount-pct-input"
                  className="h-10"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="iida-discount-amt" className="text-xs text-muted-foreground">
                  Fixed-dollar discount (AUD)
                </Label>
                <Input
                  id="iida-discount-amt"
                  type="number"
                  min="0"
                  step="100"
                  placeholder="e.g. 500"
                  value={formData.iida_member_discount_amount}
                  onChange={(e) => setFormData({ ...formData, iida_member_discount_amount: e.target.value })}
                  data-testid="iida-discount-amt-input"
                  className="h-10"
                />
              </div>
              <p className="sm:col-span-2 text-xs text-muted-foreground">
                Leave both at 0 to offer no IIDA-member discount for this event.
              </p>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card className="mb-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Sponsor Payment Details
              </CardTitle>
              <CardDescription>
                Shown on sponsorship invitation letters (bank details, invoice reference, etc.).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder={`BSB: 062-001\nAccount: 1234 5678\nAccount name: Infinity Imaging\nReference: SPONSOR-2026-03`}
                rows={4}
                value={formData.payment_details}
                onChange={(e) => setFormData({ ...formData, payment_details: e.target.value })}
                data-testid="event-payment-details-input"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/events')}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="gap-2"
              data-testid="save-event-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Update Event' : 'Create Event'}
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Copy tiers from another event — picker dialog */}
        <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="copy-tiers-dialog">
            <DialogHeader>
              <DialogTitle>Copy tiers from another event</DialogTitle>
              <DialogDescription>
                Pick an event you've previously configured tiers for. The full tier set (name, amount, benefits) will be brought over — you can tweak it freely afterwards.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[420px] overflow-y-auto -mx-2 px-2">
              {copyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : copySourceEvents.length === 0 ? (
                <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-8 text-center">
                  No other events with tiers yet. Configure tiers on at least one other event to copy from.
                </div>
              ) : (
                <div className="space-y-2" data-testid="copy-tiers-list">
                  {copySourceEvents.map((ev) => {
                    const tierCount = (ev.sponsorship_tiers || []).length;
                    const previewNames = (ev.sponsorship_tiers || [])
                      .map((t) => t.tier_name)
                      .filter(Boolean)
                      .slice(0, 4)
                      .join(' &middot; ');
                    return (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={() => handleCopySourceSelect(ev)}
                        className="w-full text-left p-3 rounded-md border border-border bg-card hover:bg-muted/50 transition-colors"
                        data-testid={`copy-source-${ev.id}`}
                      >
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">{ev.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {ev.date} &middot; {tierCount} tier{tierCount === 1 ? '' : 's'}
                            </p>
                          </div>
                        </div>
                        {previewNames && (
                          <p
                            className="text-xs text-muted-foreground mt-1.5"
                            dangerouslySetInnerHTML={{ __html: previewNames }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCopyDialogOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Replace-or-append confirm (only shown when current event already has tiers) */}
        <AlertDialog open={!!pendingCopySource} onOpenChange={(o) => { if (!o) setPendingCopySource(null); }}>
          <AlertDialogContent data-testid="copy-tiers-replace-append-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>This event already has tiers</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingCopySource ? (
                  <>
                    Copying <strong>{(pendingCopySource.sponsorship_tiers || []).length}</strong> tier(s) from <em>"{pendingCopySource.title}"</em>.
                    Would you like to replace your current tiers, or append the copied tiers to them?
                  </>
                ) : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <AlertDialogCancel data-testid="copy-tiers-cancel">Cancel</AlertDialogCancel>
              <Button
                type="button"
                variant="outline"
                onClick={() => applyCopy('append')}
                data-testid="copy-tiers-append"
              >
                Append
              </Button>
              <AlertDialogAction
                onClick={() => applyCopy('replace')}
                data-testid="copy-tiers-replace"
              >
                Replace
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
